import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { CalculatorDisclaimer } from "@/components/brand/CalculatorDisclaimer";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Download, Calendar } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { loadLegacyScore } from "@/lib/legacyScore.functions";
import { CareerInviteCard } from "@/components/conversion/CareerInviteCard";
import {
  DIMENSIONS,
  DIMENSION_META,
  bandOf,
  bandTone,
  type Band,
  type Dimension,
  type ImprovementAction,
} from "@/lib/legacyScore";

export const Route = createFileRoute("/tools/legacy-score/result/$token")({
  head: () => ({
    meta: [
      { title: "Your Legacy Score — Currency Thoughts" },
      {
        name: "description",
        content:
          "Your personal Legacy Score, six-dimension breakdown, and a personalized roadmap.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LegacyScoreResultPage,
});

type ScoreRow = {
  id: string;
  email: string | null;
  score_total: number;
  score_protection: number;
  score_income: number;
  score_retirement: number;
  score_estate: number;
  score_beneficiaries: number;
  score_education: number;
  previous_score: number | null;
  improvement_actions: ImprovementAction[];
  completed_at: string | null;
  created_at: string;
};

function bandClasses(band: Band): { text: string; ring: string } {
  const tone = bandTone(band);
  switch (tone) {
    case "success":
      return { text: "text-success", ring: "ring-success/40" };
    case "bronze":
      return { text: "text-bronze-600", ring: "ring-bronze-600/40" };
    case "warning":
      return { text: "text-warning", ring: "ring-warning/40" };
    case "danger":
      return { text: "text-danger", ring: "ring-danger/40" };
  }
}

function bandLabel(band: Band): string {
  switch (band) {
    case "legacy_ready":
      return "Legacy Ready";
    case "strong_foundation":
      return "Strong Foundation";
    case "meaningful_gaps":
      return "Meaningful Gaps";
    case "critical":
      return "Critical Attention Needed";
  }
}

function useCountUp(target: number, durationMs = 1500): number {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const from = 0;
    function tick(now: number) {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(from + (target - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return val;
}

function CircularGauge({ score }: { score: number }) {
  const displayed = useCountUp(score, 1500);
  const r = 130;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const offset = c * (1 - pct);
  return (
    <svg width={300} height={300} viewBox="0 0 300 300" role="img" aria-label={`Legacy Score: ${score} of 100`}>
      <circle cx="150" cy="150" r={r} fill="none" stroke="var(--color-navy-800)" strokeWidth="14" opacity="0.18" />
      <g transform="rotate(-90 150 150)">
        <circle
          cx="150"
          cy="150"
          r={r}
          fill="none"
          stroke="var(--color-bronze-600)"
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 1.5s cubic-bezier(0.22,1,0.36,1)" }}
        />
      </g>
      <text
        x="150"
        y="160"
        textAnchor="middle"
        fill="var(--color-bronze-600)"
        style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "96px" }}
      >
        {displayed}
      </text>
      <text
        x="150"
        y="200"
        textAnchor="middle"
        fill="var(--color-charcoal-500)"
        style={{ fontFamily: "var(--font-sans)", fontSize: "12px", letterSpacing: "0.22em", textTransform: "uppercase" }}
      >
        out of 100
      </text>
    </svg>
  );
}

function LegacyScoreResultPage() {
  const { token } = Route.useParams();
  const load = useServerFn(loadLegacyScore);
  const [row, setRow] = useState<ScoreRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [retakeOptIn, setRetakeOptIn] = useState(false);
  const shareSvgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    let cancel = false;
    load({ data: { token } })
      .then((res) => {
        if (cancel) return;
        if (!res.ok) setError(res.error);
        else {
          const scoreRow = res.score as unknown as ScoreRow;
          setRow(scoreRow);
          const band = bandOf((scoreRow as { score_total: number }).score_total);
          trackEvent("legacy_score_completed", { band });
        }
      })
      .catch(() => {
        if (!cancel) setError("Unable to load your score.");
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [token, load]);

  const dimensionScores = useMemo(() => {
    if (!row) return [] as { dimension: Dimension; score: number }[];
    return DIMENSIONS.map((d) => ({
      dimension: d,
      score: (row as unknown as Record<string, number>)[`score_${d}`] ?? 0,
    }));
  }, [row]);

  function handleShare() {
    const svg = shareSvgRef.current;
    if (!svg) return;
    try {
      const xml = new XMLSerializer().serializeToString(svg);
      const svg64 = btoa(unescape(encodeURIComponent(xml)));
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 1200;
        canvas.height = 630;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, 1200, 630);
        canvas.toBlob((blob) => {
          if (!blob) return;
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "legacy-score.png";
          a.click();
          URL.revokeObjectURL(url);
          toast.success("Your Legacy Score graphic was downloaded.");
        }, "image/png");
      };
      img.src = `data:image/svg+xml;base64,${svg64}`;
    } catch {
      toast.error("Couldn't generate your share graphic.");
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-50">
        <Navbar />
        <div className="mx-auto max-w-3xl px-6 py-32 text-center font-sans text-charcoal-500">
          Loading your Legacy Score…
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !row) {
    return (
      <div className="min-h-screen bg-cream-50">
        <Navbar />
        <div className="mx-auto max-w-3xl px-6 py-32 text-center">
          <h1 className="font-display text-[28px] text-navy-900">We couldn't load this score.</h1>
          <p className="mt-4 font-sans text-charcoal-500">{error || "The link may have expired."}</p>
          <div className="mt-8">
            <PrimaryButton asChild>
              <Link to="/tools/legacy-score">Start a new assessment</Link>
            </PrimaryButton>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const band = bandOf(row.score_total);
  const bandCls = bandClasses(band);
  const delta =
    typeof row.previous_score === "number" ? row.score_total - row.previous_score : null;

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />

      {/* HERO + GAUGE */}
      <section className="bg-white pt-16 pb-12">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-center">
            <Eyebrow color="bronze" align="center">
              Your Result
            </Eyebrow>
            <h1 className="mt-5 font-display font-bold text-navy-900 text-[40px] md:text-[52px] leading-tight">
              Your Legacy Score.
            </h1>
          </div>

          <div className="mt-10 flex flex-col items-center">
            <CircularGauge score={row.score_total} />
            <p
              className={cn(
                "mt-4 font-sans font-semibold uppercase tracking-[0.22em] text-[13px]",
                bandCls.text,
              )}
            >
              {bandLabel(band)}
            </p>
            {delta !== null && delta !== 0 && (
              <p
                className={cn(
                  "mt-3 inline-flex items-center gap-2 font-sans text-[13px]",
                  delta > 0 ? "text-success" : "text-danger",
                )}
              >
                {delta > 0 ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                {delta > 0 ? `+${delta}` : delta} from your last assessment
              </p>
            )}
            <p className="mt-6 max-w-[560px] text-center font-sans text-[16px] text-charcoal-500 leading-[1.7]">
              Your Legacy Score is a weighted measure across six dimensions of
              family readiness. Use it as a baseline — then watch it climb.
            </p>
          </div>
        </div>
      </section>

      {/* DIMENSION BREAKDOWN */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-6">
          <Eyebrow color="bronze">Six-dimension breakdown</Eyebrow>
          <h2 className="mt-4 font-display font-semibold text-navy-900 text-[28px] md:text-[34px]">
            Where you stand, dimension by dimension.
          </h2>

          <div className="mt-8 bg-white border border-bronze-600/30 rounded-[4px] divide-y divide-border" style={{ boxShadow: "var(--shadow-soft)" }}>
            {dimensionScores.map(({ dimension, score }) => {
              const meta = DIMENSION_META[dimension];
              return (
                <div key={dimension} className="p-6 md:p-8">
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="font-display font-semibold text-navy-900 text-[18px] md:text-[20px]">
                      {meta.label}
                    </h3>
                    <span className="inline-flex items-center rounded-full bg-bronze-600/10 px-3 py-1 font-sans font-semibold text-bronze-600 text-[13px]">
                      {score}/100
                    </span>
                  </div>
                  <div className="mt-3 h-2 w-full rounded-full bg-cream-50 overflow-hidden">
                    <div
                      className="h-full bg-bronze-600 transition-all duration-1000 ease-out"
                      style={{ width: `${Math.max(2, score)}%` }}
                    />
                  </div>
                  <p className="mt-3 font-sans text-[14px] text-charcoal-500 leading-[1.6]">
                    {meta.tagline}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="bg-cream-50 py-16">
        <div className="mx-auto max-w-3xl px-6">
          <Eyebrow color="bronze">Your personalized roadmap</Eyebrow>
          <h2 className="mt-4 font-display font-semibold text-navy-900 text-[28px] md:text-[34px]">
            The three highest-impact moves for your family.
          </h2>
          <p className="mt-3 font-sans text-[15px] text-charcoal-500 leading-[1.7]">
            These are drawn from your lowest-scoring dimensions and ranked by
            how many points each could add to your total.
          </p>

          <ol className="mt-10 space-y-6">
            {row.improvement_actions.map((action, i) => (
              <li
                key={action.id}
                className="bg-white border border-bronze-600/30 rounded-[4px] p-6 md:p-8"
                style={{ boxShadow: "var(--shadow-soft)" }}
              >
                <div className="flex items-start gap-5">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-navy-900 ring-2 ring-bronze-600/60">
                    <span className="font-display font-bold text-bronze-400 text-[20px]">
                      {i + 1}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-display font-semibold text-navy-900 text-[20px]">
                        {action.title}
                      </h3>
                      <span className="inline-flex items-center rounded-full bg-success/10 px-3 py-1 font-sans font-semibold text-success text-[12px] uppercase tracking-[0.12em]">
                        +{action.estimated_impact} points possible
                      </span>
                    </div>
                    <p className="mt-3 font-sans text-[15px] text-charcoal-900 leading-[1.7]">
                      {action.recommendation}
                    </p>
                    <div className="mt-5">
                      <PrimaryButton asChild>
                        <Link to={action.cta_href as "/book"}>{action.cta_label}</Link>
                      </PrimaryButton>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SHARE & TRACK */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-3xl px-6">
          <Eyebrow color="bronze">Share & track</Eyebrow>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <div className="border border-bronze-600/30 rounded-[4px] p-6 bg-cream-50/40">
              <h3 className="font-display font-semibold text-navy-900 text-[18px]">
                Share your score
              </h3>
              <p className="mt-2 font-sans text-[14px] text-charcoal-500 leading-[1.6]">
                Download a private graphic to share with your spouse or
                strategist.
              </p>
              <button
                type="button"
                onClick={handleShare}
                className="mt-4 inline-flex items-center gap-2 font-sans text-[13px] uppercase tracking-[0.18em] text-navy-900 hover:text-bronze-600 transition-colors"
              >
                <Download className="h-4 w-4" />
                Download graphic
              </button>
            </div>
            <div className="border border-bronze-600/30 rounded-[4px] p-6 bg-cream-50/40">
              <h3 className="font-display font-semibold text-navy-900 text-[18px]">
                Retake in 12 months
              </h3>
              <p className="mt-2 font-sans text-[14px] text-charcoal-500 leading-[1.6]">
                Most families improve their score year over year. We can remind
                you when it's time to refresh.
              </p>
              <label className="mt-4 flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={retakeOptIn}
                  onCheckedChange={(v) => {
                    setRetakeOptIn(Boolean(v));
                    if (v) toast.success("We'll remind you in 12 months.");
                  }}
                  className="mt-1"
                />
                <span className="inline-flex items-center gap-2 font-sans text-[13px] text-charcoal-900">
                  <Calendar className="h-4 w-4 text-bronze-600" />
                  Email me a reminder
                </span>
              </label>
            </div>
          </div>
          <CalculatorDisclaimer />
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="bg-navy-900 text-cream-50 py-16">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Eyebrow color="cream" align="center">
            Next step
          </Eyebrow>
          <h2 className="mt-5 font-display font-semibold text-cream-50 text-[28px] md:text-[36px]">
            Want help building your roadmap?
          </h2>
          <p className="mx-auto mt-4 max-w-[520px] font-sans text-[16px] text-cream-50/80 leading-[1.7]">
            Schedule a private walkthrough with a Currency Thoughts Strategist.
            No pitch — an educational conversation about what to do next.
          </p>
          <div className="mt-8">
            <PrimaryButton asChild size="lg">
              <Link to="/book">Schedule a walkthrough</Link>
            </PrimaryButton>
          </div>
        </div>
      </section>
      <CareerInviteCard source="score" variant="footer" />

      {/* Offscreen share SVG — 1200x630 for social sharing dimensions */}
      <svg
        ref={shareSvgRef}
        width={1200}
        height={630}
        viewBox="0 0 1200 630"
        style={{ position: "absolute", left: -99999, top: -99999 }}
        aria-hidden
      >
        <rect width="1200" height="630" fill="var(--color-navy-900)" />
        <rect x="40" y="40" width="1120" height="550" fill="none" stroke="var(--color-bronze-600)" strokeWidth="2" opacity="0.4" />
        <text x="80" y="120" fill="var(--color-bronze-400)" style={{ fontFamily: "var(--font-sans)", fontSize: "16px", letterSpacing: "0.22em", textTransform: "uppercase" }}>
          The Legacy Score
        </text>
        <text x="80" y="180" fill="var(--color-cream-50)" style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "56px" }}>
          My family's readiness:
        </text>
        <g transform="translate(80, 260)">
          <circle cx="120" cy="120" r="110" fill="none" stroke="var(--color-navy-800)" strokeWidth="14" opacity="0.6" />
          <g transform="rotate(-90 120 120)">
            <circle
              cx="120"
              cy="120"
              r="110"
              fill="none"
              stroke="var(--color-bronze-600)"
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 110}
              strokeDashoffset={2 * Math.PI * 110 * (1 - row.score_total / 100)}
            />
          </g>
          <text x="120" y="140" textAnchor="middle" fill="var(--color-bronze-400)" style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "80px" }}>
            {row.score_total}
          </text>
        </g>
        <text x="380" y="380" fill="var(--color-cream-50)" style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontStyle: "italic", fontSize: "40px" }}>
          {bandLabel(band)}
        </text>
        <text x="380" y="430" fill="var(--color-cream-50)" opacity="0.75" style={{ fontFamily: "var(--font-sans)", fontSize: "18px" }}>
          out of 100 possible points
        </text>
        <text x="80" y="560" fill="var(--color-bronze-400)" style={{ fontFamily: "var(--font-script)", fontSize: "42px" }}>
          Currency Thoughts
        </text>
        <text x="80" y="585" fill="var(--color-cream-50)" opacity="0.6" style={{ fontFamily: "var(--font-sans)", fontSize: "12px", letterSpacing: "0.22em", textTransform: "uppercase" }}>
          Building Generational Wealth
        </text>
      </svg>

      <Footer />
    </div>
  );
}